/**
 * ================================================================================================
 *  מועדי דיווח — the office's own deadlines, and the one screen where a template becomes a rule
 * ================================================================================================
 *
 * §6.15: *"מועדים רגולטוריים לא כהנחה אחידה — לפי סוג הדיווח, ההגדרות והמקור, וניתנים לתחזוקה."*
 *
 * ── WHY A TEMPLATE IS SHOWN AT ALL, RATHER THAN HIDDEN UNTIL IT IS USABLE ───────────────────────
 *
 * Four rows arrive from the server with no filing day, unverified and inactive: monthly VAT,
 * two-monthly VAT, income-tax withholding and National Insurance. A screen that hid them would be
 * telling an office this product has nothing to say about its reporting calendar. A screen that
 * showed them as broken would be wrong in the other direction.
 *
 * So they are shown AS TEMPLATES, with the lead sentence explaining what that means: the product
 * declines to assert a filing date on its own authority, and the office establishes it. That is the
 * Owner's *"אל תמציא מועדים"* rendered as a sentence somebody reads rather than as an absence they
 * have to interpret.
 *
 * ── AND WHY THE FORM OFFERS "VERIFIED" EVEN WHEN IT WILL BE REFUSED ────────────────────────────
 *
 * The server refuses `verified` without a day, a source and an effective date, by name:
 * `needs_day`, `needs_source`, `needs_effective_from`. This screen does not disable the control to
 * prevent that. Disabling it would mean this screen deciding what "verified" requires — a second
 * implementation of a rule the database already holds in
 * `CK_reminder_rule_verified_has_evidence` — and the two would drift.
 *
 * What the screen DOES do is say the requirement up front (`deadlines.verifyHint`), so the refusal
 * is a reminder rather than a surprise. The same reasoning as the task screen's cancel control.
 */

import { useCallback, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { classifyActionError, type ActionFailure } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { useWorkContext } from '../shell/ContextProvider';
import { AppShell } from '../shell/AppShell';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import type { ReminderRuleView } from '../api/contract';
import { routes } from '../routes';

/**
 * The four the server's CHECK constraint admits. Not a list this screen invented.
 *
 * NAMED `PERIOD_KIND_OPTIONS` AND NOT `PERIODS`, and the distinction is the one 09 §3.4 turns
 * on: these are the four SHAPES a reporting period can have, offered as a field on a rule being
 * edited. They are not the accounting periods a person works in -- those live in the context bar
 * and are chosen in exactly one place.
 */
const PERIOD_KIND_OPTIONS: readonly { readonly id: string; readonly label: TextKey }[] = [
  { id: 'monthly', label: 'deadlines.period.monthly' },
  { id: 'bi_monthly', label: 'deadlines.period.bi_monthly' },
  { id: 'quarterly', label: 'deadlines.period.quarterly' },
  { id: 'annual', label: 'deadlines.period.annual' },
];

/** What the form holds while it is open. Strings, because that is what an input gives back. */
interface Draft {
  readonly displayNameHe: string;
  readonly periodKind: string;
  readonly dueDayOfPeriod: string;
  readonly dueMonthOffset: string;
  readonly leadDays: string;
  readonly sourceNote: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
  readonly verified: boolean;
  readonly isActive: boolean;
}

function draftFrom(rule: ReminderRuleView): Draft {
  return {
    displayNameHe: rule.displayNameHe,
    periodKind: rule.periodKind,
    dueDayOfPeriod: rule.dueDayOfPeriod === null ? '' : String(rule.dueDayOfPeriod),
    dueMonthOffset: String(rule.dueMonthOffset),
    leadDays: String(rule.leadDays),
    sourceNote: rule.sourceNote ?? '',
    effectiveFrom: rule.effectiveFrom ?? '',
    effectiveTo: rule.effectiveTo ?? '',
    verified: rule.verificationState === 'verified',
    isActive: rule.isActive,
  };
}

/**
 * An empty numeric field is `null` and NOT zero.
 *
 * Zero is a meaningful value for the month offset — a filing due inside the period it covers — so
 * mapping "the operator cleared the box" to 0 would silently change the rule's meaning. The day has
 * no valid zero, and `null` there is exactly the template state.
 */
function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : null;
}

function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function TenantReminderRulesScreen(): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);

  const read = useCallback(() => callOperation('tenant.reminder_rule.list', {}), []);
  const list = useServerData(read);

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('deadlines.title')} requires="TENANT" actual={ctx.surface} />;
  }

  function open(rule: ReminderRuleView): void {
    setOpenCode(rule.ruleCode);
    setDraft(draftFrom(rule));
    setFailure(null);
    setSaved(false);
  }

  function close(): void {
    setOpenCode(null);
    setDraft(null);
  }

  function save(event: FormEvent, rule: ReminderRuleView): void {
    event.preventDefault();
    if (draft === null) return;
    setSending(true);
    setFailure(null);
    setSaved(false);
    void callOperation('tenant.reminder_rule.put', {
      /*
       * THE WHOLE RULE, EVERY TIME. This is a PUT keyed on `ruleCode`: a partial body would make
       * "the day is now unknown" indistinguishable from "the day was not in this request", and the
       * first is a real state a person may want to return a rule to.
       *
       * `ruleCode` is the rule's own code even when the row being edited is a PLATFORM TEMPLATE —
       * which is how a template becomes this office's rule. The server writes the office's own row
       * and never touches the platform one; the resolver then prefers the office's.
       */
      body: {
        ruleCode: rule.ruleCode,
        displayNameHe: draft.displayNameHe,
        periodKind: draft.periodKind,
        dueDayOfPeriod: numberOrNull(draft.dueDayOfPeriod),
        dueMonthOffset: numberOrNull(draft.dueMonthOffset) ?? 1,
        leadDays: numberOrNull(draft.leadDays) ?? 0,
        sourceNote: textOrNull(draft.sourceNote),
        isActive: draft.isActive,
        verificationState: draft.verified ? 'verified' : 'unverified',
        effectiveFrom: textOrNull(draft.effectiveFrom),
        effectiveTo: textOrNull(draft.effectiveTo),
      },
    })
      .then(() => {
        setSaved(true);
        close();
        list.reload();
      })
      .catch((error: unknown) => {
        setFailure(classifyActionError(error));
      })
      .finally(() => {
        setSending(false);
      });
  }

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build if one
         Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      screenId="tenant-reminder-rules"
      titleHe={t('deadlines.title')}
      backToParent={{ labelHe: t('settings.title'), href: routes.tenantAdmin }}
    >
      <p className="screen-lead">{t('deadlines.lead')}</p>
      <p className="form__note">{t('deadlines.templateNote')}</p>

      {saved ? <StateBlock titleHe={t('deadlines.title')} bodyHe={t('deadlines.saved')} /> : null}
      {failure === null ? null : <ActionFailureNotice failure={failure} />}

      <ServerRead<{ rules: readonly ReminderRuleView[] }>
        result={list.result}
        whatHe={t('deadlines.title')}
      >
        {(data) =>
          data.rules.length === 0 ? (
            <StateBlock
              titleHe={t('deadlines.title')}
              bodyHe={t('deadlines.empty')}
              tone="empty"
            />
          ) : (
            <ul className="list list--items">
              {data.rules.map((rule) => (
                <li key={rule.ruleCode} className="list__row">
                  <RuleRow
                    rule={rule}
                    expanded={openCode === rule.ruleCode}
                    draft={openCode === rule.ruleCode ? draft : null}
                    sending={sending}
                    onOpen={() => {
                      if (openCode === rule.ruleCode) close();
                      else open(rule);
                    }}
                    onChange={setDraft}
                    onSave={(event) => save(event, rule)}
                  />
                </li>
              ))}
            </ul>
          )
        }
      </ServerRead>
    </AppShell>
  );
}

function RuleRow(props: {
  readonly rule: ReminderRuleView;
  readonly expanded: boolean;
  readonly draft: Draft | null;
  readonly sending: boolean;
  readonly onOpen: () => void;
  readonly onChange: (draft: Draft) => void;
  readonly onSave: (event: FormEvent) => void;
}): JSX.Element {
  const { t } = useLocale();
  const { rule } = props;
  const periodLabel =
    PERIOD_KIND_OPTIONS.find((period) => period.id === rule.periodKind)?.label ??
    'deadlines.field.period';

  return (
    <>
      <div className="list__main">
        <h3 className="list__title">{rule.displayNameHe}</h3>
        <span className="chip-row">
          <span className="chip">
            {t(rule.isOwnRule ? 'deadlines.mark.own' : 'deadlines.mark.template')}
          </span>
          <span className="chip">
            {t(
              rule.verificationState === 'verified'
                ? 'deadlines.mark.verified'
                : 'deadlines.mark.unverified',
            )}
          </span>
          <span className="chip">
            {t(rule.isActive ? 'deadlines.mark.active' : 'deadlines.mark.inactive')}
          </span>
        </span>
        <p className="list__meta">
          {`${t('deadlines.field.period')}: ${t(periodLabel)}`}
          {' · '}
          {/*
            THE DAY, OR THE HONEST ABSENCE OF ONE. `טרם נקבע יום דיווח` is not an error state and is
            not rendered as one: it is what every shipped template says, and what a rule says until
            somebody looks the date up.
          */}
          {rule.dueDayOfPeriod === null
            ? t('deadlines.noDay')
            : `${t('deadlines.field.day')}: ${String(rule.dueDayOfPeriod)}`}
        </p>
        <p className="list__meta">
          {rule.sourceNote === null ? t('deadlines.noSource') : rule.sourceNote}
        </p>
        {rule.effectiveFrom === null ? null : (
          <p className="list__meta" dir="ltr">
            {`${t('deadlines.field.effectiveFrom')}: ${rule.effectiveFrom}`}
            {rule.effectiveTo === null ? '' : ` — ${rule.effectiveTo}`}
          </p>
        )}
      </div>

      <div className="actions-row">
        <button type="button" className="btn btn--quiet" onClick={props.onOpen}>
          {t(props.expanded ? 'deadlines.editClose' : 'deadlines.edit')}
        </button>
      </div>

      {props.expanded && props.draft !== null ? (
        <RuleForm
          draft={props.draft}
          sending={props.sending}
          onChange={props.onChange}
          onSave={props.onSave}
        />
      ) : null}
    </>
  );
}

function RuleForm(props: {
  readonly draft: Draft;
  readonly sending: boolean;
  readonly onChange: (draft: Draft) => void;
  readonly onSave: (event: FormEvent) => void;
}): JSX.Element {
  const { t } = useLocale();
  const { draft } = props;
  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => {
    props.onChange({ ...draft, [key]: value });
  };

  return (
    <form className="form" onSubmit={props.onSave}>
      <p className="form__note">{t('deadlines.verifyHint')}</p>

      <label className="field">
        <span className="field__label">{t('deadlines.field.name')}</span>
        <input
          className="field__input"
          value={draft.displayNameHe}
          maxLength={200}
          onChange={(event) => set('displayNameHe', event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">{t('deadlines.field.period')}</span>
        <select
          className="field__input"
          name="periodKind"
          value={draft.periodKind}
          onChange={(event) => set('periodKind', event.target.value)}
        >
          {PERIOD_KIND_OPTIONS.map((period) => (
            <option key={period.id} value={period.id}>
              {t(period.label)}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span className="field__label">{t('deadlines.field.day')}</span>
        {/*
          `min`/`max` MIRROR THE COLUMN'S CHECK AND DO NOT REPLACE IT. The server validates the day
          at run time and refuses 0 or 32 by name — *"טיפוס TypeScript אינו בקרת אבטחה לבקשה
          חיצונית"*, and neither is an input attribute.
        */}
        <input
          className="field__input"
          type="number"
          min={1}
          max={31}
          value={draft.dueDayOfPeriod}
          onChange={(event) => set('dueDayOfPeriod', event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">{t('deadlines.field.monthOffset')}</span>
        <input
          className="field__input"
          type="number"
          min={0}
          max={24}
          value={draft.dueMonthOffset}
          onChange={(event) => set('dueMonthOffset', event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">{t('deadlines.field.lead')}</span>
        <input
          className="field__input"
          type="number"
          min={0}
          max={365}
          value={draft.leadDays}
          onChange={(event) => set('leadDays', event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">{t('deadlines.field.source')}</span>
        <textarea
          className="field__input"
          rows={2}
          maxLength={400}
          value={draft.sourceNote}
          onChange={(event) => set('sourceNote', event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">{t('deadlines.field.effectiveFrom')}</span>
        <input
          className="field__input"
          type="date"
          value={draft.effectiveFrom}
          onChange={(event) => set('effectiveFrom', event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">{t('deadlines.field.effectiveTo')}</span>
        <input
          className="field__input"
          type="date"
          value={draft.effectiveTo}
          onChange={(event) => set('effectiveTo', event.target.value)}
        />
      </label>

      <label className="choice">
        <input
          type="checkbox"
          checked={draft.verified}
          onChange={(event) => set('verified', event.target.checked)}
        />
        <span>{t('deadlines.mark.verified')}</span>
      </label>

      <label className="choice">
        <input
          type="checkbox"
          checked={draft.isActive}
          onChange={(event) => set('isActive', event.target.checked)}
        />
        <span>{t('deadlines.field.activeToggle')}</span>
      </label>

      <div className="actions-row">
        <button type="submit" className="btn btn--primary" disabled={props.sending}>
          {t('deadlines.save')}
        </button>
      </div>
    </form>
  );
}
